import { z } from 'zod'
import { storefrontShopSchema } from './shopRegistry'

export const newsletterInterests = [
  { id: 'wohnen', label: 'Wohnen & Dekorieren' },
  { id: 'schlafen', label: 'Schlafen & Bettwäsche' },
  { id: 'tisch', label: 'Tisch & Genießen' },
  { id: 'kueche', label: 'Küche' },
  { id: 'bad', label: 'Bad & Wellness' },
  { id: 'lifestyle', label: 'Lifestyle & Geschenke' },
  { id: 'angebote', label: 'Angebote' },
  { id: 'neuheiten', label: 'Neuheiten' },
] as const
export const newsletterInterestSchema = z.enum(['wohnen', 'schlafen', 'tisch', 'kueche', 'bad', 'lifestyle', 'angebote', 'neuheiten'])
export const newsletterInterestsSchema = z.array(newsletterInterestSchema).max(8).transform(items => [...new Set(items)])
export const newsletterFrequencySchema = z.enum(['weekly', 'monthly'])
export type NewsletterFrequency = z.infer<typeof newsletterFrequencySchema>
export type NewsletterInterest = z.infer<typeof newsletterInterestSchema>
export const NEWSLETTER_CONSENT_VERSION = '2026-11-02-v1'
export const NEWSLETTER_CONSENT_TEXT = 'Ich möchte per E-Mail Neuigkeiten, Inspiration und Angebote dieses Shops erhalten. Meine Interessen sind freiwillig. Ich kann meine Einwilligung jederzeit im Kundenkonto oder über den Abmeldelink in jeder E-Mail widerrufen.'

const httpsUrl = z.string().max(2000).refine(value => {
  if (!value) return true
  try { const url = new URL(value); return url.protocol === 'https:' && !url.username && !url.password }
  catch { return false }
}, 'Bitte eine vollständige HTTPS-Adresse eingeben.')
const line = (max: number, min = 0) => z.string().trim().min(min).max(max).refine(value => !/[\r\n]/.test(value), 'Zeilenumbrüche sind hier nicht erlaubt.')
export const newsletterContentSchema = z.object({
  subject: line(180, 1), preheader: line(250).default(''),
  heading: z.string().trim().min(1).max(200), body: z.string().trim().min(1).max(20000),
  heroImageUrl: httpsUrl.default(''), buttonLabel: line(100).default(''), buttonUrl: httpsUrl.default(''),
  accentColor: z.string().regex(/^#[0-9a-f]{6}$/i).default('#102d54'),
}).strict().refine(value => !!value.buttonLabel === !!value.buttonUrl, 'Button-Text und Link bitte gemeinsam angeben.')
export type NewsletterContent = z.infer<typeof newsletterContentSchema>
export const newsletterSegmentSchema = z.object({
  interests: newsletterInterestsSchema.default([]), match: z.enum(['any', 'all']).default('any'),
}).strict()
export type NewsletterSegment = z.infer<typeof newsletterSegmentSchema>
export const newsletterSettingsSchema = z.object({
  enabled: z.boolean(), fromName: line(100, 1), fromEmail: z.string().trim().email().max(254),
  replyTo: z.string().trim().email().max(254), postalAddress: z.string().trim().min(10).max(1000),
  ratePerMinute: z.number().int().min(1).max(3), dailyLimit: z.number().int().min(1).max(200),
  minDaysBetween: z.number().int().min(7).max(90), dkimSelector: z.string().trim().min(1).max(100).regex(/^[a-zA-Z0-9_-]+$/),
}).strict()
export type NewsletterSettings = z.infer<typeof newsletterSettingsSchema>
export type NewsletterReadiness = { ready: boolean; issues: string[] }
export type NewsletterSubscriptionStatus = 'not_subscribed' | 'pending' | 'subscribed' | 'unsubscribed' | 'suppressed'
export interface NewsletterSubscription {
  id?: string; email: string; status: NewsletterSubscriptionStatus; interests: NewsletterInterest[]; frequency: NewsletterFrequency;
  confirmedAt?: string; updatedAt?: string; suppressionReason?: string;
}
export type NewsletterCampaignStatus = 'draft' | 'scheduled' | 'sending' | 'paused' | 'completed' | 'cancelled'
export interface NewsletterCampaign {
  id: string; shopId: number; revision: number; name: string; status: NewsletterCampaignStatus;
  content: NewsletterContent; segment: NewsletterSegment; scheduledAt?: string; createdAt: string; updatedAt: string;
  counts: { total: number; sent: number; skipped: number; failed: number; uncertain: number; pending: number };
  lastError?: string;
}
export interface NewsletterOverview {
  settings: NewsletterSettings; readiness: NewsletterReadiness;
  stats: { pending: number; subscribed: number; unsubscribed: number; suppressed: number };
}
export const newsletterShopRequestSchema = z.object({ shopId: storefrontShopSchema })
export const newsletterIdRequestSchema = newsletterShopRequestSchema.extend({ id: z.string().regex(/^[a-f\d]{24}$/i) })
export const newsletterRevisionRequestSchema = newsletterIdRequestSchema.extend({ revision: z.number().int().min(0) })
export const newsletterSaveRequestSchema = newsletterShopRequestSchema.extend({
  id: z.string().regex(/^[a-f\d]{24}$/i).optional(), revision: z.number().int().min(0).optional(),
  name: z.string().trim().min(1).max(200), content: newsletterContentSchema, segment: newsletterSegmentSchema,
}).strict()
